import React from 'react';
import { Box, Container, Grid, Link, Typography, Divider, IconButton } from '@mui/material';
import { GitHub, LinkedIn, Facebook, Email } from '@mui/icons-material';

interface AppFooterProps {
  version: string;
}

export const AppFooter: React.FC<AppFooterProps> = ({ version }) => {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        py: 4,
        px: 2,
        backgroundColor: 'var(--theme-background)',
        borderTop: '1px solid var(--theme-divider)',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 2, color: 'var(--theme-primary)' }}>
              QwickPDF
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'var(--theme-text-secondary)' }}>
              Create professional PDFs from Markdown with complete privacy. Everything stays in your browser.
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--theme-text-secondary)' }}>
              Version {version}
            </Typography>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h6" sx={{ mb: 2, color: 'var(--theme-text)' }}>
              Legal
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link
                href="/terms"
                underline="hover"
                sx={{ color: 'var(--theme-text-secondary)', '&:hover': { color: 'var(--theme-primary)' } }}
              >
                Terms & Conditions
              </Link>
              <Link
                href="/privacy"
                underline="hover"
                sx={{ color: 'var(--theme-text-secondary)', '&:hover': { color: 'var(--theme-primary)' } }}
              >
                Privacy Policy
              </Link>
              <Link
                href="https://qwickapps.com/pages/contact"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ color: 'var(--theme-text-secondary)', '&:hover': { color: 'var(--theme-primary)' } }}
              >
                Contact Us
              </Link>
            </Box>
          </Grid>

          {/* Social Links */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h6" sx={{ mb: 2, color: 'var(--theme-text)' }}>
              Connect
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <IconButton
                component="a"
                href="https://github.com/qwickapps/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                sx={{ color: 'var(--theme-text-secondary)', '&:hover': { color: 'var(--theme-primary)' } }}
              >
                <GitHub />
              </IconButton>
              <IconButton
                component="a"
                href="https://www.linkedin.com/company/qwickapps"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                sx={{ color: 'var(--theme-text-secondary)', '&:hover': { color: 'var(--theme-primary)' } }}
              >
                <LinkedIn />
              </IconButton>
              <IconButton
                component="a"
                href="https://fb.me/qwickapps"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                sx={{ color: 'var(--theme-text-secondary)', '&:hover': { color: 'var(--theme-primary)' } }}
              >
                <Facebook />
              </IconButton>
            </Box>
            <Link
              href="https://qwickapps.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'block',
                mb: 1,
                color: 'var(--theme-primary)',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Visit QwickApps.com
            </Link>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: 'var(--theme-divider)' }} />

        {/* Copyright */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ color: 'var(--theme-text-secondary)' }}>
            © {currentYear}{' '}
            <Link
              href="https://qwickapps.com/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'var(--theme-text-secondary)', textDecoration: 'none', '&:hover': { color: 'var(--theme-primary)' } }}
            >
              QwickApps
            </Link>
            . All rights reserved.
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--theme-text-secondary)' }}>
            Powered by{' '}
            <Link
              href="https://qwickapps.com/pages/products"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'var(--theme-primary)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              QwickApps React Framework
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
